import {
  LoadingOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Alert, Button, Modal } from 'antd'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import { useGongAgentAutofill } from 'features/libra/stepComponents/GongAgentAutofill'
import {
  buildRecommendationInputs,
  flattenRecommendations,
  parseInspectionRecommendations,
} from 'features/libra/stepComponents/niiReport/inspectionRecommendations'
import { useZoneData } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexPermissionsValues } from 'types'

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.5rem;
  margin-bottom: 1rem;
  container-type: inline-size;
`

const EmptyStateTitle = styled.span`
  color: ${({ theme }) => theme.colors.grayscale[100]};
  font-size: 1rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  gap: 0.5rem;

  @container (min-width: 24rem) {
    flex-direction: row;
    align-items: center;
  }
`

const HeaderLabel = styled.span`
  color: ${({ theme }) => theme.colors.grayscale[200]};
  font-size: 0.875rem;
  overflow-wrap: anywhere;
`

const PrefillButton = styled(AiPrefillButton)`
  max-width: 100%;
  height: auto;
  min-height: 2.25rem;
  padding-inline: 0.5rem;
  white-space: normal;

  @container (min-width: 24rem) {
    && {
      width: auto;
    }
  }
`

const AddManuallyButton = styled(Button)`
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: 2.25rem;
  padding-inline: 0.5rem;
  white-space: normal;

  @container (min-width: 24rem) {
    width: auto;
  }

  && {
    color: ${({ theme }) => theme.forecast.defaultText};
    background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
    border-color: ${({ theme }) => theme.forecast.border2};
  }

  &&:not(:disabled):hover {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
    border-color: ${({ theme }) => theme.forecast.activeText};
  }
`

export interface NiiRecommendationsAiPanelProps {
  section: FormSection
  zoneFieldName: string
  recommendations: ReadonlyArray<string>
  onApply: (recommendations: ReadonlyArray<string>) => void
  onAddManual: () => void
  readonly?: boolean
}

const RawNiiRecommendationsAiPanel: FC<NiiRecommendationsAiPanelProps> = ({
  section,
  zoneFieldName,
  recommendations,
  onApply,
  onAddManual,
  readonly,
}) => {
  const { zones } = useZoneData(zoneFieldName)
  const [responseState, setResponseState] = useState<
    'empty' | 'invalid' | null
  >(null)

  const confirmedZones = useMemo(
    () => zones.filter((zone) => !zone.ai_suggested),
    [zones]
  )

  const inputs = useMemo(
    () => buildRecommendationInputs(confirmedZones),
    [confirmedZones]
  )

  const onFieldValues = useCallback(
    (fieldValues: FieldValues) => {
      const parsed = parseInspectionRecommendations(fieldValues)
      const lines = flattenRecommendations(parsed, confirmedZones)
      if (lines.length === 0) {
        setResponseState(parsed.state === 'invalid' ? 'invalid' : 'empty')
        return
      }
      setResponseState(null)
      onApply(lines)
    },
    [confirmedZones, onApply]
  )

  const { autofill, loading, error } = useGongAgentAutofill({
    sectionId: section.id,
    onFieldValues,
    inputs,
  })

  const existingCount = useMemo(
    () => recommendations.filter((line) => line.trim().length > 0).length,
    [recommendations]
  )

  const handleClick = useCallback(() => {
    if (existingCount === 0) {
      void autofill()
      return
    }
    Modal.confirm({
      title: 'Replace the existing recommendations?',
      content: `This will overwrite ${existingCount} recommendation${
        existingCount === 1 ? '' : 's'
      } currently in the box. Nothing is saved until you save the report.`,
      okText: 'Replace',
      cancelText: 'Cancel',
      onOk: () => autofill(),
    })
  }, [existingCount, autofill])

  const permissions = (section.agentPermissions ??
    []) as RolodexPermissionsValues[]

  if (readonly) return null

  const noZones = confirmedZones.length === 0
  const tooltip = noZones
    ? 'Confirm at least one zone in NII Zones before generating recommendations.'
    : 'Pre-fill per-zone inspection recommendations with AI from each zone’s strategy type, required coverage, and damage mechanisms.'
  const zoneSummary = noZones
    ? 'No confirmed zones yet'
    : `Based on ${confirmedZones.length} confirmed zone${
        confirmedZones.length === 1 ? '' : 's'
      }`

  return (
    <Panel>
      {recommendations.length === 0 && (
        <EmptyStateTitle>No recommendations yet</EmptyStateTitle>
      )}
      <HeaderLabel>{zoneSummary}</HeaderLabel>
      <ActionRow>
        <AiPrefillGate permissions={permissions}>
          <PrefillButton
            block
            type='primary'
            icon={loading ? <LoadingOutlined spin /> : <ThunderboltOutlined />}
            onClick={handleClick}
            disabled={loading || noZones}
            tooltip={tooltip}>
            {prefillLabel({
              loading,
              hasRun: existingCount > 0,
            })}
          </PrefillButton>
        </AiPrefillGate>
        <AddManuallyButton icon={<PlusOutlined />} onClick={onAddManual}>
          Add manually
        </AddManuallyButton>
      </ActionRow>

      {error !== null && <Alert type='error' showIcon message={error} />}

      {responseState === 'invalid' && (
        <Alert
          type='error'
          showIcon
          message='The agent response could not be processed. Try pre-filling with AI again or add recommendations manually.'
        />
      )}

      {responseState === 'empty' && (
        <Alert
          type='info'
          showIcon
          message='No recommendations were supported by the available zone and inspection evidence. Complete the zone inputs or add recommendations manually.'
        />
      )}
    </Panel>
  )
}

export const NiiRecommendationsAiPanel = RawNiiRecommendationsAiPanel
